import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Briefcase, AlertTriangle, X } from 'lucide-react';
import MyPostingCard from './MyPostingCard';
import PostOpportunityModal from './PostOpportunityModal';
import { useMyPostings, useCloseOpportunity, useDeleteOpportunity } from '../hooks/use-opportunities';

// ── Inline confirmation modal (replaces window.confirm) ───────────────────────
const ConfirmDialog = ({ isOpen, title, message, confirmLabel, confirmClass, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-fadeIn">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={20} className="text-orange-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-1">{title}</h3>
            <p className="text-sm text-gray-500 leading-relaxed">{message}</p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X size={18} />
          </button>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Skeleton Loader ────────────────────────────────────────────────────────────
const PostingCardSkeleton = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm animate-pulse">
    <div className="flex justify-between mb-4">
      <div className="h-6 bg-gray-200 rounded w-56" />
      <div className="h-5 bg-gray-100 rounded w-6" />
    </div>
    <div className="flex gap-4 mb-4">
      <div className="h-4 bg-gray-100 rounded w-32" />
      <div className="h-4 bg-gray-100 rounded w-24" />
    </div>
    <div className="flex gap-2 mb-5">
      <div className="h-6 bg-gray-100 rounded-full w-20" />
      <div className="h-6 bg-gray-100 rounded-full w-28" />
    </div>
    <div className="flex justify-between items-center pt-2 border-t border-gray-50">
      <div className="flex gap-4">
        <div className="h-4 bg-gray-100 rounded w-20" />
        <div className="h-4 bg-gray-100 rounded w-24" />
      </div>
      <div className="h-9 bg-gray-200 rounded-lg w-32" />
    </div>
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const MyPostings = () => {
  const navigate = useNavigate();
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { type: 'close'|'delete', id }

  const { data, isLoading, isError } = useMyPostings();
  const { mutate: closeOpp } = useCloseOpportunity();
  const { mutate: deleteOpp } = useDeleteOpportunity();

  const postings = data?.data || [];

  const handleCloseRequest = (id) => {
    setConfirmDialog({ type: 'close', id });
  };

  const handleDeleteRequest = (id) => {
    setConfirmDialog({ type: 'delete', id });
  };

  const handleConfirm = () => {
    if (!confirmDialog) return;
    if (confirmDialog.type === 'close') {
      closeOpp(confirmDialog.id);
    } else {
      deleteOpp(confirmDialog.id);
    }
    setConfirmDialog(null);
  };

  return (
    <div className="flex-1 max-w-5xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Postings</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your veterinary job postings</p>
        </div>
        <button
          id="post-new-opportunity"
          onClick={() => setShowPostModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#6b46c1] rounded-xl hover:bg-[#553098] transition-colors shadow-sm"
        >
          <Plus size={16} /> Post a Job
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-6">
          {[1, 2].map((i) => <PostingCardSkeleton key={i} />)}
        </div>
      )}

      {/* Error */}
      {isError && <div className="text-center py-16 text-red-500 text-sm">Error loading your postings.</div>}

      {/* Empty state */}
      {!isLoading && postings.length === 0 && (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Briefcase size={28} className="text-[#6b46c1]" />
          </div>
          <p className="text-gray-900 font-semibold text-lg mb-1">No postings yet</p>
          <p className="text-sm text-gray-500 mb-6">Share your first veterinary opportunity with students</p>
          <button
            id="post-first-opportunity"
            onClick={() => setShowPostModal(true)}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-[#6b46c1] rounded-xl hover:bg-[#553098] transition-colors"
          >
            Post Your First Position
          </button>
        </div>
      )}

      {/* Posting cards */}
      {!isLoading && postings.length > 0 && (
        <div className="space-y-6">
          {postings.map((opp) => (
            <MyPostingCard
              key={opp.id}
              opportunity={opp}
              onViewApplicants={() => navigate(`/opportunities/${opp.id}/applicants`)}
              onEdit={(o) => setEditingOpportunity(o)}
              onClose={handleCloseRequest}
              onDelete={handleDeleteRequest}
            />
          ))}
        </div>
      )}

      {/* Post/Edit Modal */}
      <PostOpportunityModal
        isOpen={showPostModal || !!editingOpportunity}
        mode={editingOpportunity ? 'edit' : 'create'}
        initialData={editingOpportunity}
        onClose={() => {
          setShowPostModal(false);
          setEditingOpportunity(null);
        }}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!confirmDialog}
        title={confirmDialog?.type === 'delete' ? 'Delete Posting?' : 'Close Position?'}
        message={
          confirmDialog?.type === 'delete'
            ? 'This will permanently delete this job posting and all its applications. This cannot be undone.'
            : 'Students will no longer be able to apply to this position. You can re-open it later by editing.'
        }
        confirmLabel={confirmDialog?.type === 'delete' ? 'Delete' : 'Close Position'}
        confirmClass={
          confirmDialog?.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-500 hover:bg-orange-600'
        }
        onConfirm={handleConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
};

export default MyPostings;
